import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown } from "lucide-react";

// FAQ data
const faqData = [
  {
    question: "How long does a typical website or app project take?",
    answer:
      "Most standard websites take 4–8 weeks; complex apps may take 8–12 weeks. We provide timelines after the discovery phase.",
  },
  {
    question: "What’s included in your website design and development package?",
    answer:
      "Our package includes responsive design, development, SEO setup, performance optimization, and post-launch support.",
  },
  {
    question: "Do you offer both website and mobile app development?",
    answer:
      "Yes, we specialize in both web and mobile apps — from design to deployment, including iOS, Android, and PWAs.",
  },
  {
    question: "How do you ensure my website performs well in search rankings?",
    answer:
      "We follow SEO best practices including semantic HTML, fast loading speeds, mobile optimization, structured data, and on-page SEO.",
  },
  {
    question: "Will my website be mobile-friendly and SEO-optimized?",
    answer:
      "Absolutely. All our sites are mobile-first, responsive, and built with SEO fundamentals baked in from day one.",
  },
  {
    question: "Can you help align my digital presence with our business goals?",
    answer:
      "Yes — our strategy team works closely with you to map digital initiatives to your business objectives.",
  },
  {
    question: "What tools or platforms do you use for data analysis and dashboards?",
    answer:
      "We use tools like Google Data Studio, Power BI, and custom dashboards using D3.js or Chart.js for data-driven insights.",
  },
  {
    question: "Do you offer UI/UX design separately from development?",
    answer:
      "Yes — we offer standalone UI/UX services including Figma prototypes, user flows, and design systems for handoff.",
  },
  {
    question: "Do you provide ongoing support or maintenance after launch?",
    answer:
      "We offer flexible maintenance plans covering updates, backups, monitoring, security patches, and content changes.",
  },
  {
    question: "What is the cost of a custom website or digital product?",
    answer:
      "Pricing depends on scope and complexity. After a consultation, we’ll provide a tailored quote with transparent pricing.",
  },
];

// FAQ item component inside same file
const FAQItem = ({ question, answer }: { question: string; answer: string }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="group border border-gray-200 rounded-lg overflow-hidden transition-all duration-500">
      <button
        className={`w-full px-6 py-4 text-left flex justify-between items-center 
          bg-blue-100 rounded-lg 
          transition-all duration-500 
          group-hover:bg-green-600`}
        onClick={() => setIsOpen(!isOpen)}
      >
        <span className="font-medium text-gray-800 group-hover:text-white transition-all duration-300">
          {question}
        </span>
        <ChevronDown
          className={`w-5 h-5 text-gray-600 transition-transform duration-300 group-hover:text-white ${
            isOpen ? "rotate-180" : ""
          }`}
        />
      </button>

      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
          >
            <div className="px-6 pt-4 pb-6 text-gray-600 text-sm leading-relaxed bg-gray-50">
              {answer}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};



// FAQ Section
const FAQSection = () => {
  return (
    <section className="py-20 bg-white">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          className="text-center mb-12"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4 relative">
            Frequently Asked <span className="text-blue-600">Questions</span>
            <div className="absolute -bottom-2 left-1/2 transform -translate-x-1/2 w-24 h-1 bg-gradient-to-r from-blue-600 to-teal-500 rounded-full"></div>
          </h2>
          <p className="text-gray-600 max-w-2xl mx-auto">
            Answers to some of the most common queries we get from our clients.
          </p>
        </motion.div>

        <div className="space-y-1">
          {faqData.map((item, index) => (
            <FAQItem key={index} question={item.question} answer={item.answer} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default FAQSection;
